import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Tooltip } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import {
  llmAgent,
  resources,
  type FlatViewAnalyticsEntities,
  type FlatViewAnalyticsResult,
} from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import type { CustomViewWidget } from '../../core/types/data-models';
import {
  formatSeriesValue,
  mergeWidgetRequest,
  numericSeriesKeys,
  resolveWidgetSeries,
  rowLabel,
  unwrapRequest,
  widgetContentHeight,
  type CustomViewSelection,
} from './widget-utils';

interface WidgetCardProps {
  widget: CustomViewWidget;
  /** View-level applied filters, merged into the widget's analytics request. */
  selection: CustomViewSelection;
  onEdit: () => void;
  onDelete: () => void;
}

/**
 * One cell of the custom-view grid: title + description header with an
 * edit/delete menu, and a content area rendered from the widget's
 * `contentDefinition` — a chart/table over its flat-view analytics
 * request, or a static markdown block.
 */
export function WidgetCard({ widget, selection, onEdit, onDelete }: WidgetCardProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const queryClient = useQueryClient();
  const def = widget.contentDefinition;
  const isMarkdown = def.type === 'markdown';
  // Content area fills the widget's grid-row span (see CustomViewPage's grid).
  const contentHeight = widgetContentHeight(widget.rows);

  const dataQuery = useQuery({
    queryKey: ['custom-view-widget-data', widget.id, widget.updatedAt, selection],
    queryFn: () =>
      resources.flatViewAnalytics(widget.dataSource, mergeWidgetRequest(widget, selection)),
    enabled: !isMarkdown,
    placeholderData: keepPreviousData,
  });

  // The backend regenerates the analytics request via the LLM agent after
  // a create/update and stamps the job id on the definition. While present,
  // poll the job and show a progress indicator; once the job reaches a
  // terminal state, refetch the widgets so the generated request (and the
  // cleared marker) land. Two delayed refetches cover the small gap between
  // the job finishing and the backend writing the row.
  const generationJobId = def.generationJobId;
  const generationJob = useQuery({
    queryKey: ['llm-job', generationJobId],
    queryFn: () => llmAgent.getJob(generationJobId as string),
    enabled: Boolean(generationJobId),
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      return state === 'completed' || state === 'failed' || state === 'cancelled'
        ? false
        : 1500;
    },
  });
  const generationState = generationJob.data?.state;
  useEffect(() => {
    if (!generationJobId) return;
    if (
      generationState !== 'completed' &&
      generationState !== 'failed' &&
      generationState !== 'cancelled'
    ) {
      return;
    }
    const refetch = () =>
      queryClient.invalidateQueries({
        queryKey: ['custom-view-widgets', widget.customViewId],
      });
    const timers = [setTimeout(refetch, 1000), setTimeout(refetch, 4000)];
    return () => timers.forEach(clearTimeout);
  }, [generationJobId, generationState, queryClient, widget.customViewId]);

  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} noWrap>
              {widget.title}
            </Typography>
            {widget.description && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {widget.description}
              </Typography>
            )}
          </Box>
          {generationJobId && (
            <Tooltip
              title={
                generationState === 'failed' || generationState === 'cancelled'
                  ? 'Analytics request generation failed — the widget keeps its previous request.'
                  : 'AI is generating this widget’s analytics request…'
              }
            >
              <CircularProgress size={16} sx={{ mt: 0.75 }} />
            </Tooltip>
          )}
          <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            open={Boolean(menuAnchor)}
            anchorEl={menuAnchor}
            onClose={() => setMenuAnchor(null)}
          >
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                onEdit();
              }}
            >
              Edit
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                onDelete();
              }}
              sx={{ color: 'error.main' }}
            >
              Delete
            </MenuItem>
          </Menu>
        </Stack>

        {isMarkdown ? (
          <Box sx={{ height: contentHeight, overflow: 'auto' }}>
            <ReactMarkdown>{def.markdown ?? ''}</ReactMarkdown>
          </Box>
        ) : (
          <WidgetData
            widget={widget}
            contentHeight={contentHeight}
            result={dataQuery.data}
            loading={dataQuery.isPending}
            error={dataQuery.isError ? extractErrorMessage(dataQuery.error) : null}
          />
        )}
      </CardContent>
    </Card>
  );
}

function WidgetData({
  widget,
  contentHeight,
  result,
  loading,
  error,
}: {
  widget: CustomViewWidget;
  contentHeight: number;
  result: FlatViewAnalyticsResult | undefined;
  loading: boolean;
  error: string | null;
}) {
  const def = widget.contentDefinition;
  const groupBy = useMemo(
    () => ((unwrapRequest(def.request ?? {}).groupBy as string[] | undefined) ?? []),
    [def.request],
  );
  const rows = result?.rows ?? [];
  const entities = result?.entities;

  const labels = useMemo(
    () => rows.map((r) => rowLabel(r, groupBy[0], entities)),
    [rows, groupBy, entities],
  );
  const seriesKeys = useMemo(() => numericSeriesKeys(rows, groupBy), [rows, groupBy]);
  // What actually gets plotted: declared derived series (ratios etc.) when
  // present, else every numeric aggregation key as its own series.
  const resolved = useMemo(
    () => resolveWidgetSeries(def, rows, seriesKeys),
    [def, rows, seriesKeys],
  );

  if (loading) {
    return (
      <Box
        sx={{
          height: contentHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ height: contentHeight, overflow: 'auto' }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  if (rows.length === 0) {
    return (
      <Box
        sx={{
          height: contentHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No data for the current filters.
        </Typography>
      </Box>
    );
  }

  // --- display options (compiled from the widget's style instructions) ----
  const display = def.display ?? {};
  const chartColors =
    display.colors && display.colors.length > 0 ? display.colors : undefined;
  const legendHidden = display.legend === false;
  const caption = display.caption;
  const isChart = def.type === 'bar' || def.type === 'line' || def.type === 'pie';
  const split = isChart && display.layout === 'chart-with-summary';

  const renderChart = (height: number) => {
    if (def.type === 'bar' || def.type === 'line') {
      const series = resolved.map((s) => ({
        label: s.label,
        data: s.values,
        valueFormatter: (v: number | null) => formatSeriesValue(v, s.format),
      }));
      const xAxis = [{ scaleType: 'band' as const, data: labels }];
      // A pure-percentage chart reads best on a fixed 0-100 axis.
      const allPercent = resolved.length > 0 && resolved.every((s) => s.format === 'percent');
      const common = {
        height,
        xAxis,
        series,
        colors: chartColors,
        ...(allPercent ? { yAxis: [{ min: 0, max: 100 }] } : {}),
        slotProps: { legend: { hidden: legendHidden } },
      };
      return def.type === 'bar' ? <BarChart {...common} /> : <LineChart {...common} />;
    }
    const first = resolved[0];
    return (
      <PieChart
        height={height}
        colors={chartColors}
        // In the split layout the summary list replaces the legend.
        slotProps={{ legend: { hidden: legendHidden || split } }}
        series={[
          {
            data: rows.map((_r, i) => ({
              id: i,
              value: first ? first.values[i] : 0,
              label: labels[i],
            })),
            valueFormatter: (item: { value: number }) =>
              formatSeriesValue(item.value, first?.format ?? 'number'),
          },
        ]}
      />
    );
  };

  const captionNode = caption ? (
    <Typography component="div" variant="body2" color="text.secondary">
      <ReactMarkdown>{caption}</ReactMarkdown>
    </Typography>
  ) : null;

  if (isChart && split) {
    // Split layout: chart in one half, caption + results list in the other.
    return (
      <Stack direction="row" spacing={2} sx={{ height: contentHeight }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>{renderChart(contentHeight)}</Box>
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
          {captionNode}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                {resolved.map((s) => (
                  <TableCell key={s.label} align="right">
                    {s.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((_row, i) => (
                <TableRow key={i}>
                  <TableCell>{labels[i]}</TableCell>
                  {resolved.map((s) => (
                    <TableCell key={s.label} align="right">
                      {formatSeriesValue(s.values[i], s.format)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Stack>
    );
  }

  if (isChart) {
    const chartHeight = captionNode ? contentHeight - 56 : contentHeight;
    return (
      <Box sx={{ height: contentHeight }}>
        {renderChart(chartHeight)}
        {captionNode && <Box sx={{ height: 56, overflow: 'auto' }}>{captionNode}</Box>}
      </Box>
    );
  }

  // 'table' (and any unknown type) — raw rows with enriched group labels.
  const columns = rows[0] ? Object.keys(rows[0]) : [];
  return (
    <Box sx={{ height: contentHeight, overflow: 'auto' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell key={c}>{c}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i}>
              {columns.map((c) => (
                <TableCell key={c}>{formatCell(row[c], c, groupBy, row, entities)}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

/**
 * Table cell formatter: group-by ids render as their enriched entity name;
 * topN arrays render as a comma-joined list (each id resolved via the
 * alias-keyed entity map when present).
 */
function formatCell(
  value: unknown,
  column: string,
  groupBy: string[],
  row: Record<string, unknown>,
  entities: FlatViewAnalyticsEntities | undefined,
): string {
  if (Array.isArray(value)) {
    return value
      .map((v) => {
        const name = entities?.[column]?.[String(v)]?.name;
        return typeof name === 'string' && name ? name : String(v);
      })
      .join(', ');
  }
  if (groupBy.includes(column)) {
    return rowLabel(row as never, column, entities);
  }
  return value == null ? '—' : String(value);
}
